import { parseLocalDate, formatDateString } from "./utils";
import { ResourceTimelineView } from "./views/resource_timeline_view";
import { MonthView } from "./views/month_view";

const VIEW_TYPES = ["week", "month"];

// CalendarScheduler — framework-agnostic calendar component.
//
// Public API:
//   new CalendarScheduler({ element, date, view, span, resources, events, ...options })
//   calendar.setData({ resources, events })
//   calendar.setView("week" | "month")
//   calendar.setSpan(1 | 2)
//   calendar.setDate("YYYY-MM-DD")
//   calendar.shiftDate(direction)          -- ±1 step (week/fortnight or month)
//   calendar.goToToday()
//   calendar.refresh()                     -- re-render in place
//   calendar.destroy()                     -- tear down DOM and listeners
//   calendar.on(name, handler)             -- subscribe to "calendar:<name>" events
//   calendar.off(name, handler)
//
// Events dispatched (all bubble from `element`):
//   calendar:view-change      { oldView, newView }
//   calendar:span-change      { oldSpan, newSpan, view }
//   calendar:date-change      { oldDate, newDate, view, span }
//   calendar:slot-click       { resourceId, startTime, endTime }   (Week)
//   calendar:date-cell-click  { date }                              (Month — drill-into-day signal)
//   calendar:event-click      { kind, id, appointmentId|blockId }
//   calendar:event-move       { id, kind, originalStart, newStart, originalEnd, newEnd, originalResourceId, newResourceId }
//   calendar:event-resize     { id, kind, originalEnd, newEnd }
//
// The component is "dumb": it renders, dispatches events, and never mutates remote state.
// Consumers wire up persistence by listening for these events from outside.
export class CalendarScheduler {
  /**
   * @param {object} options
   * @param {HTMLElement} options.element - Mount point. Required.
   * @param {string} options.date         - YYYY-MM-DD. Defaults to today.
   * @param {string} [options.view]       - "week" | "month". Default "week".
   * @param {number} [options.span]       - Weeks shown in week view, 1 or 2. Default 1.
   * @param {Array}  [options.resources]  - [{ id, name, avatarUrl }]. Default [].
   * @param {Array}  [options.events]     - calendar items. Default [].
   */
  constructor(options = {}) {
    if (!options.element || !(options.element instanceof HTMLElement)) {
      throw new Error("CalendarScheduler: `element` option is required");
    }

    this.element = options.element;
    this.date = options.date || formatDateString(new Date());
    this.view = VIEW_TYPES.includes(options.view) ? options.view : "week";
    this.resources = options.resources || [];
    this.events = options.events || [];
    this.span = options.span === 2 ? 2 : 1;

    this._activeView = null;

    // Mark element so the CSS variables and bubbling-event selector lookups work.
    this.element.classList.add("calendar-scheduler-root");
    this.element.dataset.calendarRoot = "true";

    // Compact-mode (mobile) detection. Re-render when the breakpoint flips so
    // views can swap between desktop multi-column and mobile single-column layouts.
    this._compactMql = window.matchMedia("(max-width: 1023px)");
    this._onCompactChange = () => this._build();
    if (this._compactMql.addEventListener) {
      this._compactMql.addEventListener("change", this._onCompactChange);
    } else {
      // Safari < 14 fallback.
      this._compactMql.addListener(this._onCompactChange);
    }

    this._build();
  }

  // True when the viewport is below the lg breakpoint. Views read this to decide
  // between desktop and mobile layouts.
  get compact() {
    return this._compactMql.matches;
  }

  // ---- public API ----

  setData({ resources, events } = {}) {
    if (Array.isArray(resources)) this.resources = resources;
    if (Array.isArray(events)) this.events = events;
    this._activeView?.setData();
  }

  setView(view) {
    if (!VIEW_TYPES.includes(view)) return;
    if (this.view === view) return;
    const oldView = this.view;
    this.view = view;
    this._emit("view-change", { oldView, newView: view });
    this._build();
  }

  setSpan(span) {
    const next = span === 2 ? 2 : 1;
    if (next === this.span) return;
    const oldSpan = this.span;
    this.span = next;
    this._emit("span-change", { oldSpan, newSpan: next, view: this.view });
    this._build();
  }

  setDate(newDate) {
    if (!newDate || newDate === this.date) return;
    const oldDate = this.date;
    this.date = newDate;
    this._emit("date-change", { oldDate, newDate, view: this.view, span: this.span });
    this._build();
  }

  shiftDate(direction) {
    const current = parseLocalDate(this.date);
    if (this.view === "month") current.setMonth(current.getMonth() + direction);
    else current.setDate(current.getDate() + direction * 7 * this.span);
    this.setDate(formatDateString(current));
  }

  goToToday() {
    this.setDate(formatDateString(new Date()));
  }

  refresh() {
    this._build();
  }

  destroy() {
    this._activeView?.destroy();
    this._activeView = null;
    if (this._compactMql && this._onCompactChange) {
      if (this._compactMql.removeEventListener) {
        this._compactMql.removeEventListener("change", this._onCompactChange);
      } else {
        this._compactMql.removeListener(this._onCompactChange);
      }
    }
    this.element.replaceChildren();
    this.element.classList.remove("calendar-scheduler-root");
    delete this.element.dataset.calendarRoot;
  }

  on(name, handler) {
    this.element.addEventListener(`calendar:${name}`, handler);
  }

  off(name, handler) {
    this.element.removeEventListener(`calendar:${name}`, handler);
  }

  // ---- internal ----

  _build() {
    this._activeView?.destroy();
    this._activeView = null;
    this.element.replaceChildren();

    if (this.view === "week") {
      this._activeView = new ResourceTimelineView(this);
    } else if (this.view === "month") {
      this._activeView = new MonthView(this);
    }

    this._activeView?.mount(this.element);
  }

  _emit(name, detail) {
    this.element.dispatchEvent(
      new CustomEvent(`calendar:${name}`, { detail, bubbles: true })
    );
  }
}
